import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NewsletterItem } from '../models/newsletter.model';
import { NewsletterListFacade } from '../list/newsletter-list.facade';
import { NotificationService } from '../../../../../common/services';
import {
  NewsletterItemInitialState,
  NewsletterItemLoad,
  NewsletterItemSave,
  NewsletterItemSend,
  NewsletterTypesLoad,
} from './newsletter-item.actions';
import { NewsletterItemService } from './newsletter-item.service';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NewsletterType } from '../models/newsletter-type.model';

export interface NewsletterItemStateModel extends NewsletterItem {
  newsletterTypes: NewsletterType[];
  loaded: boolean;
}

const initialState: NewsletterItemStateModel = {
  ...new NewsletterItem(),
  newsletterTypes: [new NewsletterType()],
  loaded: false,
};

@State<NewsletterItemStateModel>({
  name: 'newsletterItemItem',
  defaults: initialState,
})
@Injectable()
export class NewsletterItemState {
  @Selector()
  static getState(state: NewsletterItemStateModel): NewsletterItemStateModel {
    return state;
  }

  @Selector()
  static getNewsletterItemId(state: NewsletterItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getNewsletterTypes(state: NewsletterItemStateModel): NewsletterType[] {
    return state.newsletterTypes;
  }

  @Selector()
  static getLoaded(state: NewsletterItemStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private newsletterItemService: NewsletterItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
    private newsletterListFacade: NewsletterListFacade,
  ) {}

  @Action(NewsletterItemLoad)
  getNewsletterItem(
    ctx: StateContext<NewsletterItemStateModel>,
    { payload }: NewsletterItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.newsletterItemService.getNewsletterItem(payload).pipe(
      map((newsletterItem: NewsletterItem) => {
        ctx.patchState({ ...newsletterItem, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(NewsletterItemSave)
  saveNewsletterItem(
    ctx: StateContext<NewsletterItemStateModel>,
    { payload }: NewsletterItemSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.newsletterItemService.saveNewsletterItem(payload).pipe(
      map(() => {
        this.notificationService.success('api.newsletter.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/newsletters');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }
  @Action(NewsletterTypesLoad)
  loadNewsletterTypes(
    ctx: StateContext<NewsletterItemStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | NewsletterItemStateModel> {
    return this.newsletterItemService.getNewsletterTypes().pipe(
      map((newsletterTypes: NewsletterType[]) => ctx.patchState({ newsletterTypes })),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(NewsletterItemSend)
  sendNewsletterItem(
    ctx: StateContext<NewsletterItemStateModel>,
    { payload }: NewsletterItemSend,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.newsletterItemService.sendNewsletterItem(payload.newsletterItemId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.newsletter.success.send');
        this.newsletterListFacade.loadAll();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(NewsletterItemInitialState)
  setInitialState(ctx: StateContext<NewsletterItemStateModel>): void {
    ctx.setState(initialState);
  }
}
